<script setup lang="ts">
// Scoreboard: league filter (whole board) + a two-pane layout.
//  - Desktop: left rail = the selected league's games (live/upcoming/final),
//    main = tabbed Standings | Stats for that league.
//  - Mobile (<=900px): no rail; tabs become Games | Standings | Stats, Games
//    default. Standings rows link to Team pages, Stats to Player pages, game
//    rows to Game pages.
interface Game {
  event_id: string; sport: string; group: string
  home_team: string; away_team: string; commence_time: string | null
  status: string; home_score: number | null; away_score: number | null; final: boolean
}
interface SportTab { key: string; title: string }
interface GamesView { sports: SportTab[]; live: Game[]; upcoming: Game[]; final: Game[]; counts: any }
interface StandingsView {
  capability: string; league: string; groups?: any[]; rankings?: any[]
}
interface StatsView { league: string; leaders: any[] }

definePageMeta({ middleware: 'auth-optional' })

const api = useApi()
const route = useRoute()
const router = useRouter()
const { user } = useAuth()

const sport = ref<string>((route.query.sport as string) || '')
const isMobile = ref(false)
// Restore the tab from the URL so returning from a Game/Team page (OS/native back
// restores the URL, and the in-page "← scoreboard" links go history-back) lands on
// the same tab. Validity in the current context (hidden Odds, mobile-only Games on
// desktop) is enforced by the watcher below.
const VALID_TABS = ['odds', 'games', 'standings', 'stats'] as const
const queryTab = String(route.query.tab || '')
const activeTab = ref<'odds' | 'games' | 'standings' | 'standings-mobile' | 'stats'>(
  (VALID_TABS as readonly string[]).includes(queryTab) ? (queryTab as any) : 'odds')

// The board is fetched as ONE unit (games + standings + stats together) and rendered off the
// single result. SSR fills it in one go; on a client league-switch useAsyncData keeps the
// PREVIOUS result until the new one resolves, so the rail + tabs + panels swap to the new
// league ATOMICALLY (one frame) instead of piecemeal as three fetches land out of phase with
// each other and the filter. All three come from ESPN (free, not credit-metered like odds)
// and are cached read-through; the backend negative-caches empty leaders.
interface BoardView { league: string; games: GamesView | null; standings: StandingsView | null; stats: StatsView | null }
const { data: board, refresh: refreshBoard } = await useAsyncData<BoardView>('scoreboard-board',
  async () => {
    const s = sport.value
    const gamesReq = api.get<GamesView>(`/scoreboard${s ? `?sport=${encodeURIComponent(s)}` : ''}`).catch(() => null)
    if (!s) return { league: '', games: await gamesReq, standings: null, stats: null }
    const [g, st, le] = await Promise.all([
      gamesReq,
      api.get<StandingsView>(`/scoreboard/standings?league=${encodeURIComponent(s)}`).catch(() => null),
      api.get<StatsView>(`/scoreboard/stats?league=${encodeURIComponent(s)}`).catch(() => null),
    ])
    return { league: s, games: g, standings: st, stats: le }
  },
  { watch: [sport] })

// the board snapshot, decomposed. `boardSport` is the league the rendered board is FOR (the
// committed league) — it trails `sport` (the filter selection) by exactly one fetch, which is
// what makes the swap atomic. Render ALL content off these, never off `sport` directly.
const games = computed(() => board.value?.games ?? null)
const standings = computed(() => board.value?.standings ?? null)
const stats = computed(() => board.value?.stats ?? null)
const boardSport = computed(() => board.value?.league ?? '')
const switching = computed(() => !!sport.value && sport.value !== boardSport.value)

const tableLabel = computed(() =>
  standings.value?.capability === 'rankings' ? 'Rankings' : 'Standings')

// Odds availability is confirmed only after OddsBoard's client-only /odds fetch reports
// back (null = not yet known). Until then, GUESS from the games view: a league with no
// live/upcoming games has no lines. Because the guess reads games.value — the SAME data
// that drives the rail/content — the Odds tab appears/disappears in the same reactive tick
// the content swaps on a league switch (they change together), instead of the tab flipping
// a beat after the rest of the board already changed. (OddsBoard stays mounted whenever a
// sport is set, so it still confirms/overrides the guess either way.)
const oddsAvailable = ref<boolean | null>(null)
const oddsLikely = computed(() => {
  const g = games.value
  return !!g && ((g.live?.length || 0) + (g.upcoming?.length || 0)) > 0
})
// self-excluded players keep the scoreboard (viewing isn't play) but never see
// an odds surface — the tab, board and slip all hide (GP-8 posture)
const showOddsTab = computed(() =>
  user.value?.self_excluded ? false
    : (oddsAvailable.value !== null ? oddsAvailable.value : oddsLikely.value))

// Standings/Stats are client-only fetches (null = not yet known). Show their tabs
// optimistically and hide once the league turns out to have no data, so leagues ESPN
// doesn't cover for these (boxing, cricket, off-season/cup tables, AFL/NRL leaders, …)
// never render a dead, empty tab.
const standingsAvailable = computed<boolean | null>(() => {
  if (standings.value == null) return null
  const cap = standings.value.capability
  if (cap === 'standings') return (standings.value.groups?.length || 0) > 0
  if (cap === 'rankings') return (standings.value.rankings?.length || 0) > 0
  return false   // 'none' -> no table or ranking for this competition
})
const showStandingsTab = computed(() => standingsAvailable.value !== false)
const statsAvailable = computed<boolean | null>(() =>
  stats.value == null ? null : (stats.value.leaders?.length || 0) > 0)
const showStatsTab = computed(() => statsAvailable.value !== false)

const subTabs = computed(() => {
  const tabs: { key: string; label: string }[] = []
  if (showOddsTab.value) tabs.push({ key: 'odds', label: 'Odds' })
  if (isMobile.value) tabs.push({ key: 'games', label: 'Games' })
  if (showStandingsTab.value) tabs.push({ key: 'standings', label: tableLabel.value })
  if (showStatsTab.value) tabs.push({ key: 'stats', label: 'Stats' })
  // desktop has no Games tab normally (the games rail is always on the left); add it
  // only as a last resort so the main pane is never tab-less (e.g. a no-odds league
  // out of season with no table/stats).
  if (!isMobile.value && !tabs.length) tabs.push({ key: 'games', label: 'Games' })
  return tabs
})
const availableTabs = computed(() => subTabs.value.map(t => t.key))
// preferred default when the user hasn't picked (or their pick went away): Odds, then
// the table (desktop) / Games (mobile), then whatever's left.
const defaultTab = computed(() => {
  const keys = availableTabs.value
  if (keys.includes('odds')) return 'odds'
  const sec = isMobile.value ? 'games' : 'standings'
  if (keys.includes(sec)) return sec
  return keys[0] || 'odds'
})

// Tab selection: follow the default until the user picks a tab, and never sit on a tab
// that isn't currently available (hidden Odds/Standings/Stats, or the mobile-only Games
// tab on desktop).
const userPickedTab = ref(VALID_TABS.includes(queryTab as any))
function pickTab(k: string) {
  userPickedTab.value = true
  activeTab.value = k as any
  // persist in the URL (replace, no history spam) so a later back-nav restores it
  router.replace({ query: { ...route.query, sport: sport.value, tab: k } })
}
// `viewportKnown` flips true after mount, when isMobile reflects the real screen.
// The mobile-only Games tab must not be corrected away during SSR (isMobile is
// false there), or a restored ?tab=games would be lost before the client knows
// it's on mobile.
const viewportKnown = ref(false)
watch([availableTabs, defaultTab, viewportKnown], () => {
  if (!userPickedTab.value) { activeTab.value = defaultTab.value as any; return }
  if (availableTabs.value.includes(activeTab.value)) return
  // a restored ?tab=games is only valid on mobile — don't correct it before the client
  // knows the viewport (SSR has isMobile=false).
  if (activeTab.value === 'games' && !viewportKnown.value) return
  activeTab.value = defaultTab.value as any
}, { immediate: true })
// when the board COMMITS to a new league, drop the old confirmed odds availability so the
// Odds tab falls back to the games-view guess until OddsBoard re-confirms — in phase with the
// board swap (not with the earlier filter click, which would flip the tab out of phase).
watch(boardSport, () => { oddsAvailable.value = null })

const leagueTabs = computed(() =>
  (games.value?.sports || []).map(s => ({ key: s.key, label: s.title || s.key })))
const leagueLabel = computed(() =>
  (games.value?.sports || []).find(s => s.key === boardSport.value)?.title || '')
// the book preselects this league via ?sport=; see all of its lines there
const seeMoreTo = computed(() => boardSport.value ? `/?sport=${encodeURIComponent(boardSport.value)}` : '/')

let mq: MediaQueryList | null = null
function applyMobile(m: boolean) { isMobile.value = m }
let pollTimer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  mq = window.matchMedia('(max-width: 900px)')
  applyMobile(mq.matches)
  viewportKnown.value = true
  mq.addEventListener('change', e => applyMobile(e.matches))
  pollTimer = setInterval(() => {
    if (document.visibilityState === 'visible') refreshBoard()
  }, 60_000)
})
onBeforeUnmount(() => { if (pollTimer) clearInterval(pollTimer) })

function setSport(s: string) {
  if (!s || s === sport.value) return
  sport.value = s
  // keep the current tab (and any other query) so a later back-nav restores both
  router.replace({ query: { ...route.query, sport: s } })
}
// like the bets lobby: no "all" filter — one league always selected (first)
watch(() => games.value?.sports, (list) => {
  if (list?.length && !list.some(s => s.key === sport.value)) setSport(list[0].key)
}, { immediate: true })
</script>

<template>
  <div>
    <AppNav title="scores" />

    <div class="wrap">
      <FilterTabs v-if="games && games.sports.length"
        :tabs="leagueTabs"
        :model-value="sport" @update:model-value="setSport($event)" />

      <div class="board" :class="{ switching }">
        <!-- LEFT RAIL (desktop): the selected league's games -->
        <aside v-if="!isMobile" class="rail">
          <GamesList v-if="games" :live="games.live" :upcoming="games.upcoming" :final="games.final" />
          <div v-else class="loading">games appear here ahead of kickoff.</div>
        </aside>

        <!-- MAIN: tabbed Odds / Standings / Stats (+ Games on mobile) -->
        <main class="content">
          <nav class="subtabs">
            <button v-for="t in subTabs" :key="t.key" class="subtab"
              :class="{ active: activeTab === t.key }" @click="pickTab(t.key)">{{ t.label }}</button>
          </nav>

          <!-- Odds tab: the league's lines + bet slip (same widget as the game
               page). Stays mounted while sport is set (even when the tab is
               hidden) so it can report whether the league has bettable lines. -->
          <section v-show="activeTab === 'odds'">
            <!-- never even mounted for an excluded player: mounting fetches paid
                 odds and puts play content in the DOM -->
            <OddsBoard v-if="boardSport && !user?.self_excluded" :sport="boardSport"
              :league-label="leagueLabel" :see-more-to="seeMoreTo" :limit="4"
              sticky-slip :is-mobile="isMobile"
              @availability="oddsAvailable = $event" />
          </section>

          <!-- Games tab: always present on mobile; on desktop only the last-resort
               fallback when no other tab is available (the rail also lists games). -->
          <section v-show="activeTab === 'games'">
            <GamesList v-if="games" :live="games.live" :upcoming="games.upcoming" :final="games.final" />
          </section>

          <section v-show="activeTab === 'standings'">
            <template v-if="standings">
              <StandingsTable v-if="standings.capability === 'standings'"
                :groups="standings.groups || []" :league="boardSport" />
              <RankingsList v-else-if="standings.capability === 'rankings'"
                :rankings="standings.rankings || []" :league="boardSport" />
              <p v-else class="no-table">
                No league table for this competition —
                <template v-if="isMobile">see the <strong>Games</strong> tab for the schedule.</template>
                <template v-else>the schedule is in the games list on the left.</template>
              </p>
            </template>
            <div v-else class="loading">loading…</div>
          </section>

          <section v-show="activeTab === 'stats'">
            <LeagueStats v-if="stats" :leaders="stats.leaders || []" :league="boardSport" />
            <div v-else class="loading">loading stats…</div>
          </section>
        </main>
      </div>
    </div>
  </div>
</template>

<style scoped>
.board { display: grid; grid-template-columns: 340px 1fr; gap: 20px; align-items: start; margin-top: 4px; }
/* brief dim while a league switch is in flight, so the (atomic) swap reads as intentional */
.board.switching { opacity: 0.5; transition: opacity 0.12s ease; }
.rail { position: sticky; top: 76px; max-height: calc(100dvh - 96px - var(--bt-bottom-safe, 0px)); overflow-y: auto; padding-right: 2px; }
.content { min-width: 0; }
.subtabs { display: flex; gap: 8px; margin-bottom: 16px; border-bottom: 1px solid var(--line); }
.subtab { padding: 9px 4px; margin-bottom: -1px; font-size: 14px; font-weight: 700; color: var(--muted);
  border-bottom: 2px solid transparent; margin-right: 18px; }
.subtab:hover { color: var(--ink); }
.subtab.active { color: var(--ink); border-bottom-color: var(--brand); }
.hint { color: var(--muted); font-size: 12px; margin-bottom: 12px; }
.no-table { color: var(--muted); text-align: center; padding: 32px 16px; line-height: 1.6; }
.no-table strong { color: var(--ink); }
.sub-block { margin-top: 22px; }
.block-h { font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--muted); margin-bottom: 10px; }
.loading { color: var(--muted); text-align: center; padding: 36px 16px; }
@media (max-width: 900px) {
  .board { grid-template-columns: 1fr; }
  .rail { display: none; }
}
</style>
